import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { Logo } from "../components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("This reset link is missing its token. Please request a new one."); return; }
    if (!email.trim()) { setError("Enter your email address."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: email.trim(), token, password });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not reset your password. Please request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#EAF7FC] px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-[1.5rem] bg-white p-8 md:p-10 shadow-[0_16px_48px_rgba(19,57,85,0.12)] border border-[rgba(19,57,85,0.06)]">
          <div className="flex items-center gap-3 mb-6">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#133955] text-white"><KeyRound size={20} /></span>
            <div>
              <h1 className="font-display text-xl font-bold text-[#133955]">Choose a New Password</h1>
              <p className="text-sm text-[#173042]/60">For your Home Pro Connect account</p>
            </div>
          </div>

          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 size={44} className="text-[#0f6b3d] mx-auto mb-4" />
              <p className="font-bold text-[#133955] text-lg mb-1">Password updated</p>
              <p className="text-sm text-[#173042]/70 mb-6">You can now sign in with your new password.</p>
              <Link to="/login" data-testid="reset-success-signin" className="inline-flex items-center justify-center rounded-full bg-[#133955] text-white px-8 py-3.5 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors">Sign In</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form" className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#133955] mb-2 uppercase tracking-wide">Email</label>
                <input data-testid="reset-email" type="email" className="hpc-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#133955] mb-2 uppercase tracking-wide">New Password</label>
                <input data-testid="reset-password-input" type="password" className="hpc-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#133955] mb-2 uppercase tracking-wide">Confirm Password</label>
                <input data-testid="reset-password-confirm" type="password" className="hpc-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat it" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6]"
              >
                {busy && <Loader2 size={18} className="animate-spin" />}
                {busy ? "Updating..." : "Set New Password"}
              </button>
              <p className="text-xs text-[#173042]/60 text-center">
                Link expired? <Link to="/login" data-testid="reset-back-to-login" className="text-[#54BCE6] font-semibold hover:text-[#133955]">Request a new one</Link>
              </p>
            </form>
          )}
        </div>
        <p className="text-center text-sm mt-6"><a href="/" className="text-[#133955] font-semibold hover:underline">← Back to site</a></p>
      </div>
    </div>
  );
}
